/**
 * Time Drift Calculator - Calculates time gained/lost between laps
 *
 * This module provides utilities for comparing lap performance by calculating
 * how much time a rider gains or loses at each point around the track.
 * Used by both mobile and web frontends.
 */

import { Lap } from './Lap'
import { GpsPoint } from '../telemetry/processed/GpsPoint'
import { calculatePointDistance } from '../../utils/distanceUtils'

/**
 * Represents a single point of time drift data
 */
export interface TimeDriftPoint {
  /** Distance from lap start in meters */
  distanceMeters: number
  /** Elapsed time at this point in the reference lap (ms) */
  referenceElapsedMs: number
  /** Time difference: positive = slower than reference, negative = faster */
  timeDeltaMs: number
  /** Smoothed time delta after applying moving average */
  smoothedTimeDeltaMs: number
  /** Rate of time change (ms per point) - positive = losing time, negative = gaining */
  timeDriftRate: number
  /** GPS point from the reference lap */
  referencePoint: GpsPoint
  /** GPS point from the comparison lap (closest match) */
  comparisonPoint: GpsPoint
}

/**
 * Result of a time drift calculation between two laps
 */
export interface TimeDriftResult {
  /** The reference (usually fastest) lap */
  referenceLap: Lap
  /** The comparison lap being analyzed */
  comparisonLap: Lap
  /** Total time difference for the full lap (ms) */
  totalTimeDeltaMs: number
  /** Array of time drift points around the lap */
  points: TimeDriftPoint[]
  /** Maximum time lost at any single point */
  maxTimeLostMs: number
  /** Maximum time gained at any single point */
  maxTimeGainedMs: number
  /** Summary statistics */
  statistics: TimeDriftStatistics
}

/**
 * Summary statistics for time drift analysis
 */
export interface TimeDriftStatistics {
  /** Average time drift rate (ms/m) */
  avgDriftRatePerMeter: number
  /** Sections where significant time was lost */
  timeLosingRegions: {
    startDistance: number
    endDistance: number
    timeLostMs: number
  }[]
  /** Sections where significant time was gained */
  timeGainingRegions: {
    startDistance: number
    endDistance: number
    timeGainedMs: number
  }[]
}

/**
 * Options for time drift calculation
 */
export interface TimeDriftOptions {
  /** Window size for smoothing (default: 15 points) */
  smoothingWindow?: number
  /** Search window for finding closest points (default: 50 points) */
  searchWindow?: number
  /** Minimum time change to consider significant (ms, default: 50) */
  significantThresholdMs?: number
}

/**
 * Find the closest GPS point by geographic proximity
 * Uses a sliding window to efficiently search without checking all points
 */
function findClosestGpsPoint(
  referencePoint: GpsPoint,
  lapPoints: GpsPoint[],
  lastClosestIndex: number,
  searchWindow: number = 50
): { closestIndex: number; distance: number } {
  const startIndex = Math.max(lastClosestIndex, 0)
  const endIndex = Math.min(lapPoints.length, startIndex + searchWindow)

  let smallestDistance = calculatePointDistance(
    referencePoint,
    lapPoints[startIndex]
  )
  let closestIndex = startIndex

  for (let i = startIndex + 1; i < endIndex; i++) {
    const currentDistance = calculatePointDistance(referencePoint, lapPoints[i])
    if (currentDistance < smallestDistance) {
      smallestDistance = currentDistance
      closestIndex = i
    }
  }

  return { closestIndex, distance: smallestDistance }
}

/**
 * Apply a moving average smoothing to time differences
 */
function smoothTimeDrift(
  points: TimeDriftPoint[],
  windowSize: number
): TimeDriftPoint[] {
  const halfWindow = Math.floor(windowSize / 2)

  return points.map((point, i) => {
    const windowStart = Math.max(0, i - halfWindow)
    const windowEnd = Math.min(points.length, i + halfWindow + 1)
    const windowPoints = points.slice(windowStart, windowEnd)

    const avgTimeDelta =
      windowPoints.reduce((sum, p) => sum + p.timeDeltaMs, 0) /
      windowPoints.length

    return {
      ...point,
      smoothedTimeDeltaMs: Math.round(avgTimeDelta),
    }
  })
}

/**
 * Calculate time drift rate (trend) at each point
 * Positive = losing time, Negative = gaining time
 */
function calculateDriftRates(
  points: TimeDriftPoint[],
  windowSize: number
): TimeDriftPoint[] {
  const halfWindow = Math.floor(windowSize / 2)

  return points.map((point, i) => {
    const windowStart = Math.max(0, i - halfWindow)
    const windowEnd = Math.min(points.length - 1, i + halfWindow)

    // Calculate rate of change over the window
    let totalChange = 0
    let changeCount = 0

    for (let j = windowStart + 1; j <= windowEnd; j++) {
      const change =
        points[j].smoothedTimeDeltaMs - points[j - 1].smoothedTimeDeltaMs
      totalChange += change
      changeCount++
    }

    const avgDriftRate = changeCount > 0 ? totalChange / changeCount : 0

    return {
      ...point,
      timeDriftRate: avgDriftRate,
    }
  })
}

/**
 * Identify regions where significant time was gained or lost
 */
function identifySignificantRegions(
  points: TimeDriftPoint[],
  thresholdMs: number
): Pick<TimeDriftStatistics, 'timeLosingRegions' | 'timeGainingRegions'> {
  const timeLosingRegions: TimeDriftStatistics['timeLosingRegions'] = []
  const timeGainingRegions: TimeDriftStatistics['timeGainingRegions'] = []

  let currentRegion: {
    type: 'losing' | 'gaining' | null
    startDistance: number
    startTimeDelta: number
  } = { type: null, startDistance: 0, startTimeDelta: 0 }

  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    const rate = point.timeDriftRate

    if (rate > thresholdMs / 10 && currentRegion.type !== 'losing') {
      // Starting a time-losing region
      if (currentRegion.type === 'gaining') {
        timeGainingRegions.push({
          startDistance: currentRegion.startDistance,
          endDistance: point.distanceMeters,
          timeGainedMs:
            currentRegion.startTimeDelta - point.smoothedTimeDeltaMs,
        })
      }
      currentRegion = {
        type: 'losing',
        startDistance: point.distanceMeters,
        startTimeDelta: point.smoothedTimeDeltaMs,
      }
    } else if (rate < -thresholdMs / 10 && currentRegion.type !== 'gaining') {
      // Starting a time-gaining region
      if (currentRegion.type === 'losing') {
        timeLosingRegions.push({
          startDistance: currentRegion.startDistance,
          endDistance: point.distanceMeters,
          timeLostMs: point.smoothedTimeDeltaMs - currentRegion.startTimeDelta,
        })
      }
      currentRegion = {
        type: 'gaining',
        startDistance: point.distanceMeters,
        startTimeDelta: point.smoothedTimeDeltaMs,
      }
    }
  }

  // Close final region
  if (points.length > 0) {
    const lastPoint = points[points.length - 1]
    if (currentRegion.type === 'losing') {
      timeLosingRegions.push({
        startDistance: currentRegion.startDistance,
        endDistance: lastPoint.distanceMeters,
        timeLostMs:
          lastPoint.smoothedTimeDeltaMs - currentRegion.startTimeDelta,
      })
    } else if (currentRegion.type === 'gaining') {
      timeGainingRegions.push({
        startDistance: currentRegion.startDistance,
        endDistance: lastPoint.distanceMeters,
        timeGainedMs:
          currentRegion.startTimeDelta - lastPoint.smoothedTimeDeltaMs,
      })
    }
  }

  return { timeLosingRegions, timeGainingRegions }
}

/**
 * Calculate time drift between two laps
 *
 * @param referenceLap - The reference lap (usually the fastest)
 * @param comparisonLap - The lap to compare against the reference
 * @param options - Calculation options
 * @returns Time drift analysis result
 *
 * @example
 * ```typescript
 * const result = calculateTimeDrift(fastestLap, otherLap)
 * console.log(`Total time lost: ${result.totalTimeDeltaMs}ms`)
 * result.points.forEach(p => {
 *   console.log(`At ${p.distanceMeters}m: ${p.timeDriftRate > 0 ? 'losing' : 'gaining'} time`)
 * })
 * ```
 */
export function calculateTimeDrift(
  referenceLap: Lap,
  comparisonLap: Lap,
  options: TimeDriftOptions = {}
): TimeDriftResult {
  const {
    smoothingWindow = 15,
    searchWindow = 50,
    significantThresholdMs = 50,
  } = options

  const referencePoints = referenceLap.getGpsPoints()
  const comparisonPoints = comparisonLap.getGpsPoints()

  if (referencePoints.length === 0 || comparisonPoints.length === 0) {
    return {
      referenceLap,
      comparisonLap,
      totalTimeDeltaMs: comparisonLap.lapTime - referenceLap.lapTime,
      points: [],
      maxTimeLostMs: 0,
      maxTimeGainedMs: 0,
      statistics: {
        avgDriftRatePerMeter: 0,
        timeLosingRegions: [],
        timeGainingRegions: [],
      },
    }
  }

  // Build raw time drift points using geographic proximity
  const rawPoints: TimeDriftPoint[] = []
  let lastClosestIndex = 0
  let maxTimeLost = 0
  let maxTimeGained = 0

  for (const refPoint of referencePoints) {
    const { closestIndex } = findClosestGpsPoint(
      refPoint,
      comparisonPoints,
      lastClosestIndex,
      searchWindow
    )
    lastClosestIndex = closestIndex

    const compPoint = comparisonPoints[closestIndex]
    const timeDelta = compPoint.elapsedMs - refPoint.elapsedMs

    // Track distance relative to lap start (GPS points are already 0-relative after pipeline processing)
    const distanceFromStart = refPoint.distanceMeters

    rawPoints.push({
      distanceMeters: distanceFromStart,
      referenceElapsedMs: refPoint.elapsedMs,
      timeDeltaMs: timeDelta,
      smoothedTimeDeltaMs: timeDelta, // Will be updated by smoothing
      timeDriftRate: 0, // Will be calculated
      referencePoint: refPoint,
      comparisonPoint: compPoint,
    })

    if (timeDelta > maxTimeLost) maxTimeLost = timeDelta
    if (timeDelta < maxTimeGained) maxTimeGained = timeDelta
  }

  // Apply smoothing
  const smoothedPoints = smoothTimeDrift(rawPoints, smoothingWindow)

  // Calculate drift rates
  const finalPoints = calculateDriftRates(smoothedPoints, smoothingWindow)

  // Identify significant regions
  const regions = identifySignificantRegions(
    finalPoints,
    significantThresholdMs
  )

  // Calculate statistics
  const lapDistance = referenceLap.getLapDistance()
  const avgDriftRate =
    lapDistance > 0
      ? (comparisonLap.lapTime - referenceLap.lapTime) / lapDistance
      : 0

  return {
    referenceLap,
    comparisonLap,
    totalTimeDeltaMs: comparisonLap.lapTime - referenceLap.lapTime,
    points: finalPoints,
    maxTimeLostMs: maxTimeLost,
    maxTimeGainedMs: Math.abs(maxTimeGained),
    statistics: {
      avgDriftRatePerMeter: avgDriftRate,
      ...regions,
    },
  }
}

/**
 * Calculate time drift for multiple laps against the fastest lap
 *
 * @param laps - Array of laps to compare
 * @param options - Calculation options
 * @returns Array of time drift results, one per comparison lap
 *
 * @example
 * ```typescript
 * const results = calculateMultiLapTimeDrift(sessionLaps)
 * results.forEach(r => {
 *   console.log(`Lap ${r.comparisonLap.lapNumber}: ${r.totalTimeDeltaMs}ms slower`)
 * })
 * ```
 */
export function calculateMultiLapTimeDrift(
  laps: Lap[],
  options: TimeDriftOptions = {}
): TimeDriftResult[] {
  if (laps.length < 2) return []

  // Find fastest lap
  const sortedLaps = [...laps].sort((a, b) => a.lapTime - b.lapTime)
  const referenceLap = sortedLaps[0]
  const comparisonLaps = sortedLaps.slice(1)

  return comparisonLaps.map((compLap) =>
    calculateTimeDrift(referenceLap, compLap, options)
  )
}

/**
 * Drift-smoothing options used when recalibrating laps onto a shared
 * comparison distance frame. Mobile's comparison cache and the web zone
 * charts must use the same window or their recalibrated frames diverge.
 */
export const RECALIBRATION_DRIFT_OPTIONS: TimeDriftOptions = {
  smoothingWindow: 15,
}

/**
 * Pick the recalibration reference: the fastest of >= 2 laps, else none
 * (a single lap stays on its raw frame — there is nothing to compare).
 */
export function selectRecalibrationReference(laps: Lap[]): Lap | undefined {
  return laps.length >= 2 ? getFastestLap(laps) : undefined
}

/**
 * Get the fastest lap from an array of laps.
 *
 * Laps without a positive, finite lap time are ignored: client-side finish-line
 * re-segmentation can emit degenerate laps (e.g. a single GPS point where
 * start === finish, so lapTime 0) which would otherwise always win the
 * comparison and become the recalibration reference frame for every other lap.
 * Returns undefined when no lap has a valid time.
 */
export function getFastestLap(laps: Lap[]): Lap | undefined {
  let fastest: Lap | undefined
  for (const lap of laps) {
    if (!Number.isFinite(lap.lapTime) || lap.lapTime <= 0) continue
    if (fastest === undefined || lap.lapTime < fastest.lapTime) {
      fastest = lap
    }
  }
  return fastest
}
